import { useTranslation } from "react-i18next";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAlertContext } from "../../../alert/AlertContext";
import { useConfirmationDialogContext } from "../../../confirmation-dialog/ConfirmationDialogContext";
import { AssetInventoryDocumentsSliceApi } from "../../api/ClientFileApi";
import { useOptionalAssetInventoryDocumentsApi } from "../../api/ClientFileApiContext";
import { ClientFileQueryKey } from "../../query/clientFileQueryKeys";

type DeleteDocument = (params: { clientId: string; documentId: string }) => Promise<void>;

interface Options<TDocument> {
  queryKey: ClientFileQueryKey;
  getDocuments: (api: AssetInventoryDocumentsSliceApi, params: { clientId: string }) => Promise<TDocument[]>;
  getDeleteDocument?: (api: AssetInventoryDocumentsSliceApi) => DeleteDocument | undefined;
}

export function useAssetInventoryDocuments<TDocument>(
  clientId: string,
  { queryKey, getDocuments, getDeleteDocument }: Options<TDocument>
) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useAlertContext();
  const { showConfirmationDialog } = useConfirmationDialogContext();
  const documentsApi = useOptionalAssetInventoryDocumentsApi();
  const deleteDocument = documentsApi ? getDeleteDocument?.(documentsApi) : undefined;

  const listQueryKey = [queryKey, clientId];

  const invalidateList = () => queryClient.invalidateQueries({ queryKey: listQueryKey });

  const { data: documents = [], isFetched } = useQuery({
    queryKey: listQueryKey,
    queryFn: () => {
      if (!documentsApi) {
        throw new Error("ClientFileApi.assetInventoryDocuments is not provided");
      }
      return getDocuments(documentsApi, { clientId });
    },
    enabled: !!documentsApi,
  });

  const deleteMutation = useMutation({
    mutationFn: ({ documentId }: { documentId: string; fileName: string }) => {
      if (!deleteDocument) {
        throw new Error("The asset-inventory document delete endpoint is not available");
      }
      return deleteDocument({ clientId, documentId });
    },
    onSuccess: (_data, { fileName }) => {
      showSuccess(t("dropzone.fileList.deleteSuccess", { fileName }));
      void invalidateList();
    },
    onError: (_error, { fileName }) => showError(t("dropzone.fileList.deleteError", { fileName })),
  });

  const markReadMutation = useMutation({
    mutationFn: ({ documentId, isRead }: { documentId: string; isRead: boolean }) => {
      if (!documentsApi?.markAssetInventoryDocumentRead) {
        throw new Error("ClientFileApi.assetInventoryDocuments.markAssetInventoryDocumentRead is not available");
      }
      return documentsApi.markAssetInventoryDocumentRead({
        clientId,
        documentId,
        markAssetInventoryDocumentReadRequest: { isRead },
      });
    },
    onSuccess: () => void invalidateList(),
  });

  const confirmDelete = (documentId: string, fileName: string) =>
    showConfirmationDialog({
      dialogTitleText: t("documents.list.deleteConfirmTitle"),
      dialogText: t("documents.list.deleteConfirm", { fileName }),
      agreeText: t("common.yes"),
      discardText: t("common.no"),
      onClose: (confirmed) => {
        if (confirmed) {
          deleteMutation.mutate({ documentId, fileName });
        }
      },
    });

  return {
    documents,
    isFetched,
    deleteDocument: deleteDocument ? confirmDelete : undefined,
    markRead: (documentId: string, isRead: boolean) => markReadMutation.mutate({ documentId, isRead }),
    isBusy: deleteMutation.isPending || markReadMutation.isPending,
  };
}
